import { useEffect, useState } from "react";
import "./MeiliDashboard.css";

const API = "https://panelcomerciantes.lagora.chat/api/meili";

export default function MeiliDashboard() {
  const [data, setData] = useState(null);
  const [index, setIndex] = useState("productos");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/diag.php?index=${encodeURIComponent(index)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (e) {
      console.error("❌ Error cargando diagnóstico:", e);
      setError("No pude obtener el diagnóstico de Meili. Revisa que el backend esté disponible.");
    } finally {
      setLoading(false);
    }
  };

  const exportJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `meili-diag-${index}-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    load();
    // No auto-reload al cambiar índice; se hace con el botón Refrescar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function hardRefresh() {
  try {
    // 1) Limpia Cache Storage (PWA / assets)
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
    // 2) Limpia Service Workers
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    // 3) Limpia storage local (opcional: filtra si quieres otro comportamiento)
    localStorage.clear();
    sessionStorage.clear();
  } catch(e) {
    console.warn('No se pudo limpiar completamente la caché:', e);
  } finally {
    // 4) Recarga "dura"
    window.location.reload(true);
  }
}

async function runReindex() {
  if (!window.confirm('¿Reindexar productos ahora? Puede tardar unos segundos.')) return;
  try {
    const res = await fetch('/api/meili/reindex_productos.php', {
      method: 'POST',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ run: 1 })
    });
    if (!res.ok) throw new Error('HTTP '+res.status);
    const data = await res.json();
    alert(`Reindex enviado: ${data.sent || 0} docs en ${data.batches || 0} lotes.`);
  } catch(e) {
    console.error(e);
    alert('❌ Error lanzando reindex: '+e.message);
  }
}


  // KPIs
  const docs = data?.index?.stats?.numberOfDocuments ?? 0;
  const usedSpace = data?.stats?.usedDatabaseSize ?? data?.stats?.databaseSize ?? 0;
  const lastUpdateRaw = data?.stats?.lastUpdate;
  const lastUpdate = lastUpdateRaw ? new Date(lastUpdateRaw).toLocaleString() : "—";
  const synonymsCount = data?.index?.settings?.synonyms ? Object.keys(data.index.settings.synonyms).length : 0;

  return (
    <div className="mdash">
      <header className="mdash-head">
        <h1>Diagnóstico MeiliSearch</h1>
        <div className="controls">
          <input
            value={index}
            onChange={(e) => setIndex(e.target.value)}
            placeholder="Índice (ej: productos)"
            aria-label="Índice"
          />
          <button onClick={load} disabled={loading} className="btn">
            {loading ? "Cargando…" : "↻ Refrescar Panel"}
          </button>
          <button onClick={exportJson} disabled={!data} className="btn secondary">
            ⭳ Exportar JSON
          </button>
          <button className="btn" onClick={hardRefresh}>🔁 Limpiar caché</button>
          <button className="btn primary" onClick={runReindex}>⚙️ Reindexar productos</button>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      {!data ? (
        <p className="muted">{loading ? "Cargando…" : "Sin datos."}</p>
      ) : (
        <>
          {/* KPIs */}
          <section className="kpis">
            <div className="kpi">
              <div className="kpi-label">Documentos</div>
              <div className="kpi-value">{docs.toLocaleString()}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Espacio usado</div>
              <div className="kpi-value">{usedSpace.toLocaleString()} B</div>
              <div className="kpi-hint">({(usedSpace / (1024 * 1024)).toFixed(2)} MB)</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Última actualización</div>
              <div className="kpi-value small">{lastUpdate}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label"># Sinónimos</div>
              <div className="kpi-value">{synonymsCount}</div>
            </div>
          </section>

          {/* Salud */}
          <section className="panel">
            <h2>Salud</h2>
            <div className="grid two">
              <div className="card">
                <p><b>Estado:</b>{" "}
                  <span className={data?.health?.status === "available" ? "badge ok" : "badge warn"}>
                    {data?.health?.status || "—"}
                  </span>
                </p>
                <p><b>Versión:</b> {data?.version?.pkgVersion || "—"}</p>
                <p><b>DB total:</b> {(data?.stats?.databaseSize ?? 0).toLocaleString()} B</p>
                <p><b>Usado:</b> {usedSpace.toLocaleString()} B</p>
                <p><b>Última actualización:</b> {lastUpdate}</p>
              </div>
              <div className="card">
                <p><b>Índice:</b> {data?.index?.uid || index}</p>
                <p><b>Indexando:</b> {String(data?.index?.stats?.isIndexing ?? false)}</p>
                <p><b>Embeddings:</b> {data?.index?.stats?.numberOfEmbeddings ?? 0}</p>
                <p><b>Docs:</b> {docs.toLocaleString()}</p>
              </div>
            </div>
          </section>

          {/* Ajustes principales */}
          <section className="panel">
            <h2>Ajustes principales</h2>
            <div className="grid three">
              <div className="card">
                <h3>Searchable</h3>
                <pre className="code">{JSON.stringify(data?.index?.settings?.searchableAttributes ?? [], null, 2)}</pre>
              </div>
              <div className="card">
                <h3>Filterable</h3>
                <pre className="code">{JSON.stringify(data?.index?.settings?.filterableAttributes ?? [], null, 2)}</pre>
              </div>
              <div className="card">
                <h3>Sortable</h3>
                <pre className="code">{JSON.stringify(data?.index?.settings?.sortableAttributes ?? [], null, 2)}</pre>
              </div>
            </div>

            <div className="grid two">
              <div className="card">
                <h3>Ranking rules</h3>
                <pre className="code">{JSON.stringify(data?.index?.settings?.rankingRules ?? [], null, 2)}</pre>
              </div>
              <div className="card">
                <h3>Sinónimos ({synonymsCount})</h3>
                <pre className="code">
                  {JSON.stringify(data?.index?.settings?.synonyms ?? {}, null, 2)}
                </pre>
              </div>
            </div>

            <div className="card">
              <h3>Distribución de campos</h3>
              <pre className="code">
                {JSON.stringify(data?.index?.stats?.fieldDistribution ?? {}, null, 2)}
              </pre>
            </div>
          </section>

          {/* Tareas recientes */}
          {data.recentTasks && !data.recentTasks.code && (
            <section className="panel">
              <h2>Tareas recientes</h2>
              <pre className="code">{JSON.stringify(data.recentTasks, null, 2)}</pre>
            </section>
          )}

          {data.recentTasks && data.recentTasks.code && (
            <section className="panel warn">
              <h2>Tareas recientes</h2>
              <p className="muted">
                {data.recentTasks.message} <br />
                <small>Código: {data.recentTasks.code}</small>
              </p>
            </section>
          )}
        </>
      )}
    </div>
  );
}
